import type { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';

import { Link } from '@sola/ark-core/atoms/link/link.tsx';
import {
  Alert,
  type AlertProps,
} from '@sola/ark-core/molecules/alert/alert.tsx';
import { knownMyUrls } from '@sola/my-sdk/constants.ts';
import type { GraphQLCommitment } from '@sola/schema/dotcom/supporters/schema.generated.ts';

import { CommitmentAvatarGroup } from '../commitment-avatar-group/commitment-avatar-group.tsx';
import { usePageAlert } from '../page-alert/page-alert-provider.tsx';

export enum MakeAPaymentAlertType {
  payAhead,
  paymentDue,
  missingPayment,
}

export interface MakeAPaymentAlertProps {
  alertType: MakeAPaymentAlertType;
  onButtonClick: () => void;
  commitments?: GraphQLCommitment[];
  amountDue?: number;
  className?: string;
  controlledTrigger?: AlertProps['controlledTrigger'];
}

const getBaseAlertProps = ({
  commitments,
  amountDue,
  translationKey,
  variant,
  showMiddleColumn,
  t,
}: Pick<MakeAPaymentAlertProps, 'commitments' | 'amountDue'> & {
  translationKey: string;
  variant: AlertProps['variant'];
  showMiddleColumn: boolean;
  t: TFunction;
}) => {
  return {
    noIcon: true,
    mobileCentered: true,
    variant,
    title: t(`my:makeAPayment.alerts.${translationKey}.title`),
    message: t(`my:makeAPayment.alerts.${translationKey}.message`, {
      amountDue: t('core:numbers.currencyCustom', {
        amount: amountDue,
      }),
    }),
    middleColumn: showMiddleColumn &&
      commitments &&
      commitments?.length > 0 && (
        <CommitmentAvatarGroup
          commitments={commitments}
          purpose={variant}
          size="lg"
          tooltipMessage={t(
            `my:makeAPayment.alerts.${translationKey}.tooltip`,
            {
              count: commitments.length,
            },
          )}
        />
      ),
  };
};

export const MakeAPaymentAlertGlobal = ({
  alertType,
  commitments,
  amountDue,
}: Omit<MakeAPaymentAlertProps, 'onButtonClick' | 'controlledTrigger'>) => {
  const { t } = useTranslation(['my', 'core']);
  const translationKey = MakeAPaymentAlertType[alertType];

  usePageAlert({
    ...getBaseAlertProps({
      commitments,
      amountDue,
      translationKey,
      variant: 'negative',
      showMiddleColumn: alertType !== MakeAPaymentAlertType.payAhead,
      t,
    }),
    id: 'homepage-missingPayment',
    priority: 50, // Lower number is lower priority
    buttonAction: (
      <Link href={knownMyUrls.billingCommitments}>
        {t('my:makeAPayment.alerts.ctaGlobal')}
      </Link>
    ),
  });

  return null;
};

export const MakeAPaymentAlert = ({
  alertType,
  commitments,
  amountDue,
  className,
  onButtonClick,
  controlledTrigger,
}: MakeAPaymentAlertProps) => {
  const { t } = useTranslation(['my', 'core']);
  const variant =
    alertType === MakeAPaymentAlertType.missingPayment ? 'negative' : 'neutral';
  const translationKey = MakeAPaymentAlertType[alertType];
  return (
    <Alert
      className={className}
      {...getBaseAlertProps({
        commitments,
        amountDue,
        translationKey,
        variant,
        showMiddleColumn: alertType !== MakeAPaymentAlertType.payAhead,
        t,
      })}
      controlledTrigger={controlledTrigger}
      buttonAction={
        <button
          onClick={onButtonClick}
          aria-label={t(`my:makeAPayment.alerts.cta`)}
          type="button"
        >
          {t(`my:makeAPayment.alerts.cta`)}
        </button>
      }
    />
  );
};
